import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import BannerImageField from './BannerImageField';
import Modal from './Modal';

const CreateLocationModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialName = '',
  allowBanner = true,
  companyId
}) => {
  const { t } = useTranslation(['locations', 'common']);
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const bannerInputRef = useRef(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setAddress('');
      setBannerFile(null);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
      setError('');
    }
  }, [isOpen, initialName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setError('');
    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('name', name.trim());
      payload.append('address', address);
      if (allowBanner && bannerFile) {
        payload.append('banner', bannerFile);
      }
      if (companyId) {
        payload.append('companyId', companyId);
      }

      const response = await axios.post('/api/locations', payload);
      onSuccess?.(response.data.data);
      onClose?.();
    } catch (err) {
      setError(formatError(err) || t('locations:failedCreate'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('locations:createLocation')}>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="create-location-name">{t('locations:nameRequired')}</label>
          <input
            id="create-location-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="create-location-address">{t('locations:addressLabel')}</label>
          <textarea
            id="create-location-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows="3"
          />
        </div>
        {allowBanner && (
          <div className="form-group">
            <label htmlFor="create-location-banner">{t('locations:bannerImage')}</label>
            <BannerImageField
              existingUrl={null}
              selectedFile={bannerFile}
              onFileSelect={setBannerFile}
              onRemove={bannerFile ? () => {
                setBannerFile(null);
                if (bannerInputRef.current) {
                  bannerInputRef.current.value = '';
                }
              } : null}
              inputRef={bannerInputRef}
              disabled={submitting}
            />
          </div>
        )}
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t('locations:creating') : t('common:create')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            {t('common:cancel')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateLocationModal;
